import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Kasa {
    kasaID: number;
    kasaAdi: string;
    kasaTuru: string; // 'Nakit' | 'Banka'
    bakiye: number;
}

interface OdemeItem {
    odemeID: number;
    cariID: number | null;
    islemTarihi: string;
    islemTakipNo: string;
    vadeTarihi: string | null;
    miktar: number;
    odemeYonu: string;   // 'Tahsilat' | 'Odeme'
    odemeDurumu: string; // 'Bekliyor' | 'Odendi'
    aciklama: string | null;
    hizmetUrunAdi: string | null;
    sozlesmeOdemesi: boolean;
    cari: { adiSoyadi: string; firmaAdi?: string | null } | null;
    ilgiliProje: { projeID: number; projeAdi: string } | null;
    sozlesme?: { sozlesmeID: number; sozlesmeTakipNo: string } | null;
}

type VadeGrubu = 'gecikmiş' | '0-30' | '31-60' | '61-90' | '90+';

// ─── Sabitler ────────────────────────────────────────────────────────────────

const GRUP_LABELS: Record<VadeGrubu, string> = {
    'gecikmiş': 'Gecikmiş',
    '0-30': '0-30 Gün',
    '31-60': '31-60 Gün',
    '61-90': '61-90 Gün',
    '90+': '90+ Gün',
};
const GRUP_ORDER: VadeGrubu[] = ['gecikmiş', '0-30', '31-60', '61-90', '90+'];

// ─── Yardımcı Fonksiyonlar ───────────────────────────────────────────────────

const fmt = (val: number) =>
    val.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const getVadeGrubu = (vadeTarihi: string | null, bugun: Date): VadeGrubu => {
    if (!vadeTarihi) return '0-30';
    const gun = Math.floor(
        (new Date(vadeTarihi).setHours(0, 0, 0, 0) - bugun.getTime()) / 86_400_000
    );
    if (gun < 0) return 'gecikmiş';
    if (gun <= 30) return '0-30';
    if (gun <= 60) return '31-60';
    if (gun <= 90) return '61-90';
    return '90+';
};

const cariAdi = (o: OdemeItem) => o.cari?.firmaAdi || o.cari?.adiSoyadi || '—';
const projeAdi = (o: OdemeItem) =>
    o.ilgiliProje?.projeAdi || (o.sozlesmeOdemesi ? 'Sözleşme' : '—');

// ─── Style Sabitleri ─────────────────────────────────────────────────────────

const TABLE: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 11,
    border: '1px solid #333',
};

const TH_BASE: React.CSSProperties = {
    padding: '6px 8px',
    border: '1px solid #666',
    fontWeight: 700,
    fontSize: 11,
    background: '#e8e8e8',
    color: '#000',
};

const TD_BASE: React.CSSProperties = {
    padding: '5px 8px',
    border: '1px solid #d1d5db',
    fontSize: 11,
    color: '#111',
    verticalAlign: 'top',
};

// ─── Alt Bileşenler ──────────────────────────────────────────────────────────

const Th: React.FC<{ children: React.ReactNode; right?: boolean; width?: string }> = ({ children, right, width }) => (
    <th style={{ ...TH_BASE, textAlign: right ? 'right' : 'left', width }}>{children}</th>
);

const Td: React.FC<{ children: React.ReactNode; right?: boolean; bold?: boolean; indent?: boolean; colSpan?: number; style?: React.CSSProperties }> = ({ children, right, bold, indent, colSpan, style }) => (
    <td colSpan={colSpan} style={{ ...TD_BASE, textAlign: right ? 'right' : 'left', fontWeight: bold ? 700 : 400, paddingLeft: indent ? 20 : 8, ...style }}>
        {children}
    </td>
);

const BolumBasligi: React.FC<{ harf: string; baslik: string; altBaslik: string }> = ({ harf, baslik, altBaslik }) => (
    <div style={{ marginBottom: 6 }}>
        <div style={{ fontWeight: 800, fontSize: 12, color: '#000', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {harf}. {baslik}
        </div>
        <div style={{ fontSize: 10, color: '#555', marginTop: 1 }}>{altBaslik}</div>
    </div>
);

const GrupBasligi: React.FC<{ grup: VadeGrubu; toplam: number; colSpan: number }> = ({ grup, toplam, colSpan }) => (
    <tr style={{ background: grup === 'gecikmiş' ? '#f0f0f0' : '#fafafa' }}>
        <td
            colSpan={colSpan}
            style={{ ...TD_BASE, fontWeight: 700, fontSize: 11, borderTop: '1.5px solid #555', borderBottom: '1px solid #999', paddingTop: 5, paddingBottom: 4, background: grup === 'gecikmiş' ? '#ebebeb' : '#f5f5f5' }}
        >
            {grup === 'gecikmiş' ? '▲ ' : '▸ '}{GRUP_LABELS[grup]}
        </td>
        <td
            style={{ ...TD_BASE, textAlign: 'right', fontWeight: 700, fontSize: 11, borderTop: '1.5px solid #555', borderBottom: '1px solid #999', background: grup === 'gecikmiş' ? '#ebebeb' : '#f5f5f5' }}
        >
            ₺{fmt(toplam)}
        </td>
    </tr>
);

// ─── Ana Bileşen ─────────────────────────────────────────────────────────────

export default function NakitAkisProj() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [kasalar, setKasalar] = useState<Kasa[]>([]);
    const [girisler, setGirisler] = useState<OdemeItem[]>([]);
    const [cikislar, setCikislar] = useState<OdemeItem[]>([]);

    const bugun = new Date();
    bugun.setHours(0, 0, 0, 0);

    const raporTarihi = new Date().toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [kasaRes, alacaklarRes, borclarRes] = await Promise.all([
                axiosInstance.get('/api/Kasalar'),
                axiosInstance.get('/api/Odemeler/nakit-akis-alacaklar'),
                axiosInstance.get('/api/Odemeler/nakit-akis-borclar'),
            ]);
            setKasalar(kasaRes.data);
            setGirisler(alacaklarRes.data);
            setCikislar(borclarRes.data);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ── Hesaplamalar ──────────────────────────────────────────────────────────

    const kasaBakiye  = kasalar.filter(k => k.kasaTuru === 'Nakit').reduce((s, k) => s + k.bakiye, 0);
    const bankaBakiye = kasalar.filter(k => k.kasaTuru === 'Banka').reduce((s, k) => s + k.bakiye, 0);
    const baslangiçBakiye = kasaBakiye + bankaBakiye;

    const toplamGiris = girisler.reduce((s, o) => s + o.miktar, 0);
    const toplamCikis = cikislar.reduce((s, o) => s + o.miktar, 0);
    const netPozisyon = baslangiçBakiye + toplamGiris - toplamCikis;

    // Gruplama
    const girisGrup = new Map<VadeGrubu, OdemeItem[]>();
    const cikisGrup = new Map<VadeGrubu, OdemeItem[]>();

    girisler.forEach(o => {
        const g = getVadeGrubu(o.vadeTarihi, bugun);
        if (!girisGrup.has(g)) girisGrup.set(g, []);
        girisGrup.get(g)!.push(o);
    });
    cikislar.forEach(o => {
        const g = getVadeGrubu(o.vadeTarihi, bugun);
        if (!cikisGrup.has(g)) cikisGrup.set(g, []);
        cikisGrup.get(g)!.push(o);
    });

    // Her grup içinde vade tarihine göre sırala
    const sortByVade = (a: OdemeItem, b: OdemeItem) =>
        (a.vadeTarihi || '9999').localeCompare(b.vadeTarihi || '9999');
    girisGrup.forEach(arr => arr.sort(sortByVade));
    cikisGrup.forEach(arr => arr.sort(sortByVade));

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div style={{ minHeight: '100vh', background: '#f1f5f9' }}>

            {/* ---- Action Bar (ekrana özel, yazdırılmaz) ---- */}
            <div className="nakit-no-print" style={{
                background: '#fff', borderBottom: '1px solid #e5e7eb',
                padding: '10px 24px', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button
                        onClick={() => navigate(-1)}
                        style={{ background: 'none', border: '1px solid #d1d5db', padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#374151' }}
                    >
                        ← Geri
                    </button>
                    <div>
                        <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>Nakit Akış ve Projeksiyon Raporu</span>
                        <span style={{ marginLeft: 10, fontSize: 11, color: '#94a3b8' }}>Vade Bazlı Cash Flow Analizi</span>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {loading && <span style={{ fontSize: 12, color: '#94a3b8' }}>{t('msg.yukleniyor')}</span>}
                    <button
                        onClick={fetchData}
                        disabled={loading}
                        style={{ background: '#f8fafc', border: '1px solid #d1d5db', padding: '5px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#374151' }}
                    >
                        ↻ Yenile
                    </button>
                    <button
                        onClick={() => window.print()}
                        style={{ background: '#1a2440', color: '#fff', border: 'none', padding: '5px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                    >
                        🖨 Yazdır / PDF
                    </button>
                </div>
            </div>

            {/* ---- Özet Kartlar (ekrana özel) ---- */}
            <div className="nakit-no-print" style={{ maxWidth: 860, margin: '20px auto 0', padding: '0 20px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                {[
                    { label: 'Başlangıç Bakiyesi', value: baslangiçBakiye, color: '#1a2440' },
                    { label: 'Toplam Alacak', value: toplamGiris, color: '#15803d' },
                    { label: 'Toplam Borç', value: toplamCikis, color: '#b91c1c' },
                    { label: 'Net Nakit Pozisyonu', value: netPozisyon, color: netPozisyon >= 0 ? '#0f766e' : '#b91c1c' },
                ].map(card => (
                    <div key={card.label} style={{ background: '#fff', borderRadius: 10, padding: '14px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', borderTop: `3px solid ${card.color}` }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{card.label}</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: card.color }}>₺{fmt(Math.abs(card.value))}</div>
                        {card.label === 'Net Nakit Pozisyonu' && (
                            <div style={{ fontSize: 10, color: netPozisyon >= 0 ? '#15803d' : '#b91c1c', marginTop: 2 }}>
                                {netPozisyon >= 0 ? '▲ Pozitif Akış' : '▼ Negatif Akış'}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* ============================================================
                YAZDIRILABILIR A4 ALAN
            ============================================================ */}
            <div
                id="nakit-print-area"
                style={{
                    maxWidth: 820, margin: '20px auto 40px', background: '#fff',
                    padding: '32px 40px', boxShadow: '0 1px 6px rgba(0,0,0,0.09)',
                    fontFamily: 'Arial, Helvetica, sans-serif',
                }}
            >
                {/* ── Rapor Başlığı ─────────────────────────────────── */}
                <div style={{ borderBottom: '2.5px solid #000', paddingBottom: 12, marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <div style={{ fontSize: 19, fontWeight: 900, color: '#000', letterSpacing: '-0.5px', textTransform: 'uppercase' }}>
                                Nakit Akış ve Projeksiyon Raporu
                            </div>
                            <div style={{ fontSize: 11, color: '#444', marginTop: 3 }}>
                                Vade Bazlı İnşaat Nakit Akış Analizi
                            </div>
                        </div>
                        <div style={{ textAlign: 'right', fontSize: 11, color: '#555', lineHeight: 1.7 }}>
                            <div>Rapor Tarihi: <strong>{raporTarihi}</strong></div>
                            <div>Referans Günü: <strong>{bugun.toLocaleDateString('tr-TR')}</strong></div>
                            <div style={{ fontSize: 10, color: '#999' }}>YapiCRM</div>
                        </div>
                    </div>
                    <div style={{ marginTop: 10, fontSize: 10, color: '#333', background: '#f5f5f5', padding: '6px 10px', border: '1px solid #ccc' }}>
                        <strong>Formül:</strong>&nbsp;
                        (Kasa + Banka Bakiyeleri) + (Gecikmiş Alacaklar) + (Vadesine Göre Sözleşme Alacakları)
                        &nbsp;−&nbsp;
                        (Vadesi Geçmiş Tedarikçi Borçları) − (Vadesine Göre Gelecek Borçlar)
                        &nbsp;= <strong>Dönem Sonu Net Nakit Pozisyonu</strong>
                    </div>
                </div>

                {/* ── A. Başlangıç Bakiyesi ─────────────────────────── */}
                <BolumBasligi harf="A" baslik="Başlangıç Bakiyesi" altBaslik="Mevcut Nakit Kasa ve Banka Hesapları" />
                <table style={TABLE}>
                    <thead>
                        <tr>
                            <Th>Hesap Adı</Th>
                            <Th>Hesap Türü</Th>
                            <Th right width="20%">Bakiye (TL)</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {kasalar.filter(k => k.kasaTuru === 'Nakit').map(k => (
                            <tr key={k.kasaID}>
                                <Td indent>{k.kasaAdi}</Td>
                                <Td>Nakit Kasa</Td>
                                <Td right>₺{fmt(k.bakiye)}</Td>
                            </tr>
                        ))}
                        {kasalar.filter(k => k.kasaTuru === 'Banka').map(k => (
                            <tr key={k.kasaID}>
                                <Td indent>{k.kasaAdi}</Td>
                                <Td>Banka Hesabı</Td>
                                <Td right>₺{fmt(k.bakiye)}</Td>
                            </tr>
                        ))}
                        {kasalar.length === 0 && (
                            <tr><td colSpan={3} style={{ ...TD_BASE, textAlign: 'center', color: '#888', fontStyle: 'italic' }}>Kasa / Banka kaydı bulunamadı</td></tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr style={{ background: '#e8e8e8' }}>
                            <td colSpan={2} style={{ ...TD_BASE, fontWeight: 700, borderTop: '1.5px solid #333' }}>
                                Nakit Kasalar: ₺{fmt(kasaBakiye)} &nbsp;|&nbsp; Banka Hesapları: ₺{fmt(bankaBakiye)}
                            </td>
                            <td style={{ ...TD_BASE, textAlign: 'right', fontWeight: 800, fontSize: 12, borderTop: '1.5px solid #333' }}>
                                ₺{fmt(baslangiçBakiye)}
                            </td>
                        </tr>
                    </tfoot>
                </table>

                <div style={{ height: 20 }} />

                {/* ── B. Nakit Girişleri ─────────────────────────────── */}
                <BolumBasligi harf="B" baslik="Nakit Girişleri — Alacaklar" altBaslik="Müşteri tahsilatları ve sözleşme bazlı bekleyen alacaklar (vade gruplarına göre)" />
                {GRUP_ORDER.every(g => !girisGrup.has(g)) ? (
                    <div style={{ padding: '10px 12px', border: '1px solid #ccc', fontSize: 11, color: '#888', fontStyle: 'italic' }}>
                        Bekleyen alacak kaydı bulunamadı.
                    </div>
                ) : (
                    <table style={TABLE}>
                        <thead>
                            <tr>
                                <Th width="22%">Cari / Müşteri</Th>
                                <Th width="18%">Proje / Sözleşme</Th>
                                <Th>{t('label.aciklama')}</Th>
                                <Th width="12%">Vade Tarihi</Th>
                                <Th right width="14%">Tutar (TL)</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {GRUP_ORDER.map(grup => {
                                const items = girisGrup.get(grup);
                                if (!items) return null;
                                const grupToplam = items.reduce((s, o) => s + o.miktar, 0);
                                return (
                                    <React.Fragment key={grup}>
                                        <GrupBasligi grup={grup} toplam={grupToplam} colSpan={4} />
                                        {items.map((o, idx) => (
                                            <tr key={o.odemeID} style={{ background: idx % 2 === 0 ? '#fff' : '#fbfbfb' }}>
                                                <Td indent>{cariAdi(o)}</Td>
                                                <Td>{projeAdi(o)}</Td>
                                                <Td>{o.aciklama || o.hizmetUrunAdi || '—'}</Td>
                                                <Td>{o.vadeTarihi ? new Date(o.vadeTarihi).toLocaleDateString('tr-TR') : '—'}</Td>
                                                <Td right>₺{fmt(o.miktar)}</Td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr style={{ background: '#d1fae5' }}>
                                <td colSpan={4} style={{ ...TD_BASE, fontWeight: 700, borderTop: '2px solid #000' }}>
                                    TOPLAM NAKİT GİRİŞİ (Alacaklar)
                                </td>
                                <td style={{ ...TD_BASE, textAlign: 'right', fontWeight: 800, fontSize: 12, borderTop: '2px solid #000' }}>
                                    ₺{fmt(toplamGiris)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                )}

                <div style={{ height: 20 }} />

                {/* ── C. Nakit Çıkışları ─────────────────────────────── */}
                <BolumBasligi harf="C" baslik="Nakit Çıkışları — Borçlar" altBaslik="Tedarikçi ve vadeli gider ödemeleri (vade gruplarına göre)" />
                {GRUP_ORDER.every(g => !cikisGrup.has(g)) ? (
                    <div style={{ padding: '10px 12px', border: '1px solid #ccc', fontSize: 11, color: '#888', fontStyle: 'italic' }}>
                        Bekleyen borç kaydı bulunamadı.
                    </div>
                ) : (
                    <table style={TABLE}>
                        <thead>
                            <tr>
                                <Th width="22%">Tedarikçi / Cari</Th>
                                <Th width="18%">Proje</Th>
                                <Th>{t('label.aciklama')}</Th>
                                <Th width="12%">Vade Tarihi</Th>
                                <Th right width="14%">Tutar (TL)</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {GRUP_ORDER.map(grup => {
                                const items = cikisGrup.get(grup);
                                if (!items) return null;
                                const grupToplam = items.reduce((s, o) => s + o.miktar, 0);
                                return (
                                    <React.Fragment key={grup}>
                                        <GrupBasligi grup={grup} toplam={grupToplam} colSpan={4} />
                                        {items.map((o, idx) => (
                                            <tr key={o.odemeID} style={{ background: idx % 2 === 0 ? '#fff' : '#fbfbfb' }}>
                                                <Td indent>{cariAdi(o)}</Td>
                                                <Td>{o.ilgiliProje?.projeAdi || '—'}</Td>
                                                <Td>{o.aciklama || o.hizmetUrunAdi || '—'}</Td>
                                                <Td>{o.vadeTarihi ? new Date(o.vadeTarihi).toLocaleDateString('tr-TR') : '—'}</Td>
                                                <Td right>₺{fmt(o.miktar)}</Td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr style={{ background: '#fee2e2' }}>
                                <td colSpan={4} style={{ ...TD_BASE, fontWeight: 700, borderTop: '2px solid #000' }}>
                                    TOPLAM NAKİT ÇIKIŞI (Borçlar)
                                </td>
                                <td style={{ ...TD_BASE, textAlign: 'right', fontWeight: 800, fontSize: 12, borderTop: '2px solid #000' }}>
                                    ₺{fmt(toplamCikis)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                )}

                <div style={{ height: 20 }} />

                {/* ── D. Dönem Sonu Net Nakit Pozisyonu ─────────────── */}
                <BolumBasligi harf="D" baslik="Dönem Sonu Net Nakit Pozisyonu" altBaslik="Formül: A + B − C = Net Nakit Pozisyonu" />
                <table style={{ ...TABLE, border: '1.5px solid #000' }}>
                    <tbody>
                        <tr style={{ background: '#fafafa' }}>
                            <td style={{ ...TD_BASE, width: '55%' }}>(A)&nbsp; Başlangıç Bakiyesi — Kasa + Banka</td>
                            <td style={{ ...TD_BASE, width: '25%', textAlign: 'right' }}>&nbsp;</td>
                            <td style={{ ...TD_BASE, width: '20%', textAlign: 'right' }}>+ ₺{fmt(baslangiçBakiye)}</td>
                        </tr>
                        <tr>
                            <td style={TD_BASE}>(B)&nbsp; Toplam Nakit Girişi — Alacaklar</td>
                            <td style={{ ...TD_BASE, textAlign: 'right' }}>₺{fmt(toplamGiris)}</td>
                            <td style={{ ...TD_BASE, textAlign: 'right' }}>&nbsp;</td>
                        </tr>
                        <tr style={{ background: '#fafafa' }}>
                            <td style={TD_BASE}>(C)&nbsp; Toplam Nakit Çıkışı — Borçlar</td>
                            <td style={{ ...TD_BASE, textAlign: 'right' }}>₺{fmt(toplamCikis)}</td>
                            <td style={{ ...TD_BASE, textAlign: 'right' }}>&nbsp;</td>
                        </tr>
                        <tr style={{ background: netPozisyon >= 0 ? '#d1fae5' : '#fee2e2', borderTop: '2px solid #000' }}>
                            <td style={{ ...TD_BASE, fontWeight: 900, fontSize: 13, borderTop: '2.5px solid #000' }}>
                                = DÖNEM SONU NET NAKİT POZİSYONU &nbsp;
                                <span style={{ fontSize: 10, fontWeight: 400, color: netPozisyon >= 0 ? '#15803d' : '#b91c1c' }}>
                                    {netPozisyon >= 0 ? '(Pozitif — Nakit Fazlası)' : '(Negatif — Nakit Açığı)'}
                                </span>
                            </td>
                            <td style={{ ...TD_BASE, borderTop: '2.5px solid #000' }}>&nbsp;</td>
                            <td style={{ ...TD_BASE, textAlign: 'right', fontWeight: 900, fontSize: 16, borderTop: '2.5px solid #000', color: netPozisyon >= 0 ? '#15803d' : '#b91c1c' }}>
                                {netPozisyon >= 0 ? '+' : '−'}₺{fmt(Math.abs(netPozisyon))}
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* ── Vade Özet Tablosu ──────────────────────────────── */}
                <div style={{ height: 20 }} />
                <BolumBasligi harf="E" baslik="Vade Özet Analizi" altBaslik="Nakit Giriş ve Çıkışlarının Vade Gruplarına Göre Karşılaştırması" />
                <table style={TABLE}>
                    <thead>
                        <tr>
                            <Th>Vade Grubu</Th>
                            <Th right>Nakit Girişi (TL)</Th>
                            <Th right>Nakit Çıkışı (TL)</Th>
                            <Th right>Net (TL)</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {GRUP_ORDER.map(grup => {
                            const g = girisGrup.get(grup)?.reduce((s, o) => s + o.miktar, 0) ?? 0;
                            const c = cikisGrup.get(grup)?.reduce((s, o) => s + o.miktar, 0) ?? 0;
                            const net = g - c;
                            return (
                                <tr key={grup} style={{ background: grup === 'gecikmiş' ? '#f5f5f5' : '#fff' }}>
                                    <Td bold={grup === 'gecikmiş'}>{GRUP_LABELS[grup]}</Td>
                                    <Td right style={{ color: g > 0 ? '#15803d' : '#888' }}>
                                        {g > 0 ? `₺${fmt(g)}` : '—'}
                                    </Td>
                                    <Td right style={{ color: c > 0 ? '#b91c1c' : '#888' }}>
                                        {c > 0 ? `₺${fmt(c)}` : '—'}
                                    </Td>
                                    <Td right bold style={{ color: net > 0 ? '#15803d' : net < 0 ? '#b91c1c' : '#888' }}>
                                        {net !== 0 ? `${net > 0 ? '+' : '−'}₺${fmt(Math.abs(net))}` : '—'}
                                    </Td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr style={{ background: '#e8e8e8', fontWeight: 700 }}>
                            <td style={{ ...TD_BASE, fontWeight: 700, borderTop: '1.5px solid #333' }}>GENEL TOPLAM</td>
                            <td style={{ ...TD_BASE, textAlign: 'right', fontWeight: 700, color: '#15803d', borderTop: '1.5px solid #333' }}>₺{fmt(toplamGiris)}</td>
                            <td style={{ ...TD_BASE, textAlign: 'right', fontWeight: 700, color: '#b91c1c', borderTop: '1.5px solid #333' }}>₺{fmt(toplamCikis)}</td>
                            <td style={{ ...TD_BASE, textAlign: 'right', fontWeight: 800, fontSize: 12, color: (toplamGiris - toplamCikis) >= 0 ? '#15803d' : '#b91c1c', borderTop: '1.5px solid #333' }}>
                                {(toplamGiris - toplamCikis) >= 0 ? '+' : '−'}₺{fmt(Math.abs(toplamGiris - toplamCikis))}
                            </td>
                        </tr>
                    </tfoot>
                </table>

                {/* ── Rapor Altbilgi ─────────────────────────────────── */}
                <div style={{ marginTop: 24, borderTop: '1px solid #bbb', paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#999' }}>
                    <span>YapiCRM — Nakit Akış ve Projeksiyon Raporu</span>
                    <span>Sadece Bekliyor durumundaki vadeli kayıtlar dahil edilmiştir.</span>
                    <span>Oluşturulma: {new Date().toLocaleString('tr-TR')}</span>
                </div>
            </div>

            {/* ---- Baskı Stilleri ---- */}
            <style>{`
                @media print {
                    .nakit-no-print { display: none !important; }
                    body { background: #fff !important; margin: 0 !important; }
                    #nakit-print-area {
                        max-width: 100% !important;
                        margin: 0 !important;
                        padding: 12mm 14mm !important;
                        box-shadow: none !important;
                        font-size: 10px !important;
                    }
                    table { page-break-inside: auto; border-color: #555 !important; }
                    tr    { page-break-inside: avoid; page-break-after: auto; }
                    thead { display: table-header-group; }
                    tfoot { display: table-footer-group; }
                    @page { size: A4 portrait; margin: 10mm; }
                }
            `}</style>
        </div>
    );
}
